// V1 source: customer/midas/components/DiagramInfoCard.tsx
// Strategy: logic-copied
// Verified no-MUI: yes
//
// V2 floating tooltip — Tier A behavioral parity with V1. Logic copied
// verbatim: cursor-follow position with edge-flip + viewport clamp, dim
// measurement via callback ref, type-translation map, fallback rendering.
//
// V1 used inline styles only (no MUI). V2 keeps inline `style` for
// position/size (computed values) and adds a Tailwind className for the
// chrome (border/shadow/typography) so it inherits the V2 palette tokens.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { DiagramHotspot } from "../../../../shared/chatBot/models/chatBotModels";
import useTranslation from "../../../../shared/hooks/useTranslation";

interface Props {
  hotspot: DiagramHotspot;
  mouseX: number;
  mouseY: number;
}

const TOOLTIP_OFFSET = 8;
const MAX_WIDTH = 420;

// Map API type values to translation keys — verbatim from V1
const TYPE_TRANSLATION_KEYS: Record<string, string> = {
  relay: "midas.artifact.diagram.typeRelay",
  contactor: "midas.artifact.diagram.typeContactor",
  module: "midas.artifact.diagram.typeModule",
  terminal_block: "midas.artifact.diagram.typeTerminalBlock",
  cable: "midas.artifact.diagram.typeCable",
  switch: "midas.artifact.diagram.typeSwitch",
  sensor: "midas.artifact.diagram.typeSensor",
  circuit_breaker: "midas.artifact.diagram.typeCircuitBreaker",
  fuse: "midas.artifact.diagram.typeFuse",
  motor: "midas.artifact.diagram.typeMotor",
  transformer: "midas.artifact.diagram.typeTransformer",
  plc_module: "midas.artifact.diagram.typePlcModule",
  valve: "midas.artifact.diagram.typeValve",
};

const DiagramInfoCardV2: React.FC<Props> = ({ hotspot, mouseX, mouseY }) => {
  const { translate } = useTranslation("chatBot");
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState({ w: 0, h: 0 });

  // Measure tooltip dimensions after render for accurate edge flipping
  const measuredRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      (tooltipRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      setDims({ w: node.offsetWidth, h: node.offsetHeight });
    }
  }, []);

  // Re-measure when content changes
  useEffect(() => {
    if (tooltipRef.current) {
      setDims({ w: tooltipRef.current.offsetWidth, h: tooltipRef.current.offsetHeight });
    }
  }, [hotspot]);

  const viewW = window.innerWidth;
  const viewH = window.innerHeight;
  // Use measured dims; fallback to conservative estimate (not full MAX_WIDTH)
  const tw = dims.w > 0 ? dims.w : 200;
  const th = dims.h > 0 ? dims.h : 60;

  // Position at cursor + offset, flip when near edges
  let left = mouseX + TOOLTIP_OFFSET;
  let top = mouseY + TOOLTIP_OFFSET;

  if (left + tw > viewW) {
    left = mouseX - tw - TOOLTIP_OFFSET;
  }
  if (top + th > viewH) {
    top = mouseY - th - TOOLTIP_OFFSET;
  }

  // Clamp to viewport so tooltip never goes off-screen
  left = Math.max(4, Math.min(left, viewW - tw - 4));
  top = Math.max(4, Math.min(top, viewH - th - 4));

  // Translate type value using i18n keys, fallback to formatted raw value
  const translationKey = hotspot.type ? TYPE_TRANSLATION_KEYS[hotspot.type] : undefined;
  const displayType = translationKey
    ? translate(translationKey)
    : hotspot.type
      ? hotspot.type.replace(/_/g, " ")
      : "";

  return (
    <div
      ref={measuredRef}
      className="pointer-events-none border border-midas-stone bg-midas-bone-light text-midas-graphite shadow-[0_2px_8px_rgba(26,26,26,0.15)] font-sora"
      style={{
        position: "fixed",
        left,
        top,
        borderRadius: 4,
        fontSize: 12,
        padding: "8px 12px",
        maxWidth: MAX_WIDTH,
        lineHeight: 1.6,
        zIndex: 1000,
        whiteSpace: "pre-line",
      }}
    >
      <b style={{ fontSize: 13 }}>{hotspot.label}</b>
      {displayType && (
        <div className="mt-[2px] text-[11px] text-midas-muted-stone">
          {translate("midas.artifact.diagram.componentType")}: {displayType}
        </div>
      )}
      {hotspot.description && (
        <div className="mt-1">
          {hotspot.description}
        </div>
      )}
    </div>
  );
};

export default React.memo(DiagramInfoCardV2);
